import { clearLimitOrders } from '../lib/limitOrders'
import { clearPendingReveal, takeOAuthResume } from '../lib/oauthBridge'
import { clearReferredBy } from '../lib/referredBy'
import { clearSocialPending } from '../lib/socialPending'

// What this browser keeps for the account that just left, cleared on Log out
// and when a signed-in session is refused (401), so nothing of it reaches the
// next account signed in on this tab (operator, 2026-11-05: another account's
// limit orders showed after logout + Google login).
//
// Cleared here:
//   - the limit-order cache (IndexedDB 'wils.limit-orders'): rows of any
//     account, keyed by order id only;
//   - the social sign-up ticket, the pending seed reveal, the OAuth resume;
//   - the referral code typed in Rewards and the invite this visit came with;
//   - the per-wallet figure caches (portfolio, activity, created tokens, last
//     known figures): keyed by address, so not shown to another account, but
//     they are this account's figures on a shared device.
// AccountSessionContext clears its own /v1/me snapshot; useAccountVault drops
// the keyring and the unlocked key when the session ends.
//
// Not cleared, on purpose:
//   - the device signing key and device seeds (accountDeviceSigning,
//     deviceWalletSeed): per account, and wiping them on logout broke signing
//     after the next login (ProfilePopover); their storage is the Security
//     session's step 6;
//   - the sell-fee outbox and the trade queues: money path, each keyed by
//     wallet and drained only for it;
//   - per-browser preferences (presets, watchlist, layout, settings).

export const ACCOUNT_RESET_EVENT = 'wt:account-reset'

const LOCAL_KEYS = ['wt:referral-code', 'wils.lastknown.v1']
const LOCAL_PREFIXES = ['wt:portfolio:', 'wt:activity:', 'wt:created:']

/** `signInFlows`: also the sign-in in progress (the social ticket, the
 *  pending reveal, the OAuth resume, the invite). Log out clears them; a
 *  refused session does not, since the tab may be mid-sign-in. */
export async function resetAccountClientState(
  options: { signInFlows: boolean } = { signInFlows: true }
): Promise<void> {
  if (options.signInFlows) {
    clearSocialPending()
    clearPendingReveal()
    takeOAuthResume()
    clearReferredBy()
  }
  clearLocalKeys()
  try {
    await clearLimitOrders()
  } catch {
    // An IndexedDB that cannot open holds nothing to leak.
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(ACCOUNT_RESET_EVENT))
}

// The account whose data the caches above hold. When another account signs
// in on this browser, they are emptied before it is drawn: whatever order a
// logout's clear and a reload still in flight land in, the next account never
// starts on them.
const CACHE_OWNER_KEY = 'wils.account-cache-owner'

export async function claimAccountCaches(accountID: string): Promise<void> {
  let owner: string | null = null
  try {
    owner = localStorage.getItem(CACHE_OWNER_KEY)
  } catch {
    return
  }
  if (owner === accountID) return
  if (owner !== null) await resetAccountClientState({ signInFlows: false })
  try {
    localStorage.setItem(CACHE_OWNER_KEY, accountID)
  } catch {
    // storage unavailable
  }
}

/** Calls `listener` after the account's client state was reset. */
export function onAccountReset(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined
  window.addEventListener(ACCOUNT_RESET_EVENT, listener)
  return () => window.removeEventListener(ACCOUNT_RESET_EVENT, listener)
}

function clearLocalKeys(): void {
  try {
    if (typeof localStorage === 'undefined') return
    for (const key of LOCAL_KEYS) localStorage.removeItem(key)
    const doomed: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && LOCAL_PREFIXES.some((prefix) => key.startsWith(prefix))) doomed.push(key)
    }
    for (const key of doomed) localStorage.removeItem(key)
  } catch {
    // storage unavailable
  }
}
